/**
 * Réconciliation des compteurs « non lus » — module PUR (aucun React, aucun
 * axios, aucun DOM). Toute la règle est ici pour être testable seule.
 *
 * LE DÉFAUT CORRIGÉ (31/08/2026). `useUnreadCounts` faisait :
 *
 *     api.get("/messages/unread-count").catch(() => ({ data: { count: 0 } }))
 *
 * Une panne réseau FABRIQUAIT donc un zéro, indiscernable d'un vrai « tout est
 * lu ». Ce zéro écrasait l'état connu, éteignait les pastilles des 8 surfaces
 * qui les affichent, et descendait jusqu'au badge du SYSTÈME (`Badge.clear()`
 * côté natif, `navigator.clearAppBadge()` côté PWA) : une panne de LECTURE
 * produisait un effet HORS de l'application, sur l'icône du téléphone.
 *
 * Deux des pastilles éteintes ne sont pas cosmétiques :
 *   - « ventes à expédier » = le seul rappel persistant de générer l'étiquette
 *     Boxtal (backend/routes/listings.py:4139) ;
 *   - « achats à confirmer » = le rappel dont la confirmation DÉBLOQUE LE
 *     VERSEMENT au vendeur (backend/routes/listings.py:4161).
 * Éteindre ces pastilles par une panne retarde une obligation logistique et de
 * l'argent réel. Aucun montant n'est calculé ici — ce module est en lecture.
 *
 * LA RÈGLE, en une phrase : une valeur qu'on n'a PAS PU LIRE n'écrase jamais
 * une valeur connue, et n'écrit jamais le badge du système.
 *
 * Asymétrie assumée (cf. cadrage) : conserver la dernière valeur connue peut
 * laisser une pastille allumée alors que l'utilisateur a lu sur un AUTRE
 * appareil. Un badge allumé à tort coûte un clic ; un badge éteint à tort coûte
 * une expédition manquée et un versement retardé. On choisit donc de conserver.
 * Et surtout : AUCUN TTL ne fait redescendre la valeur vers 0 — un TTL
 * recréerait exactement le défaut, en différé. La valeur ne baisse que sur une
 * réponse serveur réussie, une lecture explicite, ou un changement d'acteur.
 */

/** Valeur non lisible. `null` et non `0` : c'est toute la différence. */
export const INCONNU = null;

const estCompteur = (n) => typeof n === "number" && Number.isFinite(n) && n >= 0;

/**
 * Lit un compteur `{ count }` depuis un OUTCOME.
 * outcome := { ok: true, value: <réponse> } | { ok: false, kind: <string> }
 * Un `ok:false` — quelle qu'en soit la cause (réseau, timeout, 500, 401, 429) —
 * rend INCONNU. Une réponse réussie mais illisible (champ absent, `null`,
 * chaîne) rend AUSSI INCONNU : un contrat cassé n'est pas un zéro.
 */
export function lireCompteur(outcome) {
  if (!outcome || outcome.ok !== true) return INCONNU;
  const n = outcome.value?.count;
  return estCompteur(n) ? n : INCONNU;
}

/**
 * Lit `{ sales_pending, purchases_pending }` (backend/routes/listings.py:4180 —
 * les DEUX champs sont toujours renvoyés). Si l'un manque, le contrat est
 * rompu : INCONNU plutôt qu'une somme partielle qui se lirait comme un total.
 */
export function lireActions(outcome) {
  if (!outcome || outcome.ok !== true) return INCONNU;
  const v = outcome.value;
  if (!estCompteur(v?.sales_pending) || !estCompteur(v?.purchases_pending)) return INCONNU;
  return v.sales_pending + v.purchases_pending;
}

/**
 * Décide ce qu'on écrit sur le badge du SYSTÈME (icône de l'app / PWA).
 * @returns {number|null} un nombre PROUVÉ à écrire, ou `null` = NE RIEN ÉCRIRE
 *          (ni `set`, ni `clear`).
 *
 * `null` n'est pas « écrire 0 » : au démarrage, l'icône peut déjà porter le
 * compteur posé par la session PRÉCÉDENTE de l'app. L'effacer sans avoir lu le
 * serveur détruit une information qu'on n'a jamais lue. On laisse en place.
 */
export function badgeDecision({ messages, notifications }) {
  if (!estCompteur(messages) || !estCompteur(notifications)) return null;
  return messages + notifications;
}

const precedent = (prev, cle) => (estCompteur(prev?.[cle]) ? prev[cle] : 0);

/**
 * Réconcilie l'état connu avec ce qu'on vient (ou non) de lire.
 *
 * @param prev     état courant { unreadMessages, unreadNotifications, pendingActions }
 * @param outcomes { messages, notifications, actions } — trois OUTCOMEs
 * @returns {{ next, inconnus: string[], stale: boolean, badge: number|null, change: boolean }}
 */
export function reconcileCounts(prev, outcomes) {
  const messages = lireCompteur(outcomes?.messages);
  const notifications = lireCompteur(outcomes?.notifications);
  const actions = lireActions(outcomes?.actions);

  const next = {
    unreadMessages: messages === INCONNU ? precedent(prev, "unreadMessages") : messages,
    unreadNotifications:
      notifications === INCONNU ? precedent(prev, "unreadNotifications") : notifications,
    pendingActions: actions === INCONNU ? precedent(prev, "pendingActions") : actions,
  };

  const inconnus = [];
  if (messages === INCONNU) inconnus.push("messages");
  if (notifications === INCONNU) inconnus.push("notifications");
  if (actions === INCONNU) inconnus.push("actions");

  return {
    next,
    inconnus,
    stale: inconnus.length > 0,
    // Le badge ne se calcule QUE sur des valeurs prouvées — jamais sur `next`,
    // qui contient volontairement des valeurs conservées (donc non relues).
    badge: badgeDecision({ messages, notifications }),
    change:
      next.unreadMessages !== precedent(prev, "unreadMessages") ||
      next.unreadNotifications !== precedent(prev, "unreadNotifications") ||
      next.pendingActions !== precedent(prev, "pendingActions"),
  };
}

/**
 * Faut-il écrire le badge du système lors d'un changement d'acteur ?
 *
 * PIÈGE (attrapé en relecture adversariale, 31/08/2026) : `startPolling`
 * comparait simplement `state.userId !== userId`. Au TOUT PREMIER montage,
 * l'ancien id est `null` — la comparaison est donc vraie, et un `clear`
 * inconditionnel aurait effacé le compteur laissé sur l'icône par la session
 * précédente AVANT toute lecture du serveur. C'est précisément le défaut que ce
 * lot retire de NotificationBell : il se serait réintroduit trois lignes plus
 * loin.
 *
 * On distingue donc :
 *   - AUCUN acteur précédent  -> on ne sait rien   -> `null` (ne rien écrire) ;
 *   - acteur précédent DIFFÉRENT -> on SAIT que le compteur ne nous concerne
 *     plus (appareil partagé) -> `0` (effacer), seul « clear » légitime sans
 *     réponse serveur.
 *
 * @returns {0|null} 0 = effacer, null = ne rien écrire.
 */
export function badgeChangementActeur(acteurPrecedent, acteurSuivant) {
  if (!acteurPrecedent) return null;
  return acteurPrecedent === acteurSuivant ? null : 0;
}
